import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  TablePagination,
  Avatar,
  Chip,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MdEdit } from "react-icons/md";
import { IoEyeOutline } from "react-icons/io5";
import { get_services_list } from "../../../services/DashboardServices";
import { themeOrange, themeBlue } from "../../../utils/colorTheme";

const statusConfig = {
  1: { label: "Approved", bg: "#E8F5E9", color: "#2E7D32" },
  2: { label: "Rejected", bg: "#FFEBEE", color: "#C62828" },
  0: { label: "Pending", bg: "#FFF8E1", color: "#F9A825" },
};

export const VendorTable = ({ searchQuery = "" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { get_users_list_by_role_data } = useSelector(
    (state) => state.dashboard_users
  );
  const { get_services_list_data } = useSelector(
    (state) => state.dashaboard_services
  );

  const [rows, setRows] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedService, setSelectedService] = useState("");

  useEffect(() => {
    dispatch(get_services_list());
  }, [dispatch]);

  useEffect(() => {
    if (get_users_list_by_role_data?.length) {
      const sorted = [...get_users_list_by_role_data].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setAllVendors(sorted);
    }
  }, [get_users_list_by_role_data]);

  useEffect(() => {
    let filtered = allVendors;

    if (selectedService) {
      filtered = filtered.filter((vendor) =>
        vendor?.services?.some(
          (s) => s?.service?.id === selectedService || s?.service?.name === selectedService
        )
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          `${v?.first_name} ${v?.last_name}`.toLowerCase().includes(q) ||
          v?.email?.toLowerCase().includes(q) ||
          v?.phone?.includes(q)
      );
    }

    setRows(filtered);
    setPage(0);
  }, [selectedService, allVendors, searchQuery]);

  return (
    <Box>
      {/* Service Filter */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 2, borderBottom: "1px solid #f0f0f0" }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#666" }}>
            Service:
          </Typography>
          <FormControl size="small">
            <Select
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              displayEmpty
              sx={{
                minWidth: 200,
                borderRadius: "8px",
                fontSize: "13px",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: themeOrange },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: themeOrange },
              }}
            >
              <MenuItem value="">All Services</MenuItem>
              {get_services_list_data?.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Typography sx={{ fontSize: "12px", color: "#aaa" }}>
          {rows.length} result{rows.length !== 1 ? "s" : ""}
        </Typography>
      </Stack>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {["Pro", "Service", "Contact", "Status", "Actions"].map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#999",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    borderBottom: "1px solid #f0f0f0",
                    py: 1.5,
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <Typography sx={{ color: "#bbb", fontSize: "14px" }}>
                    No pros found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, i) => {
                  const status = statusConfig[+row?.status] || statusConfig[0];
                  return (
                    <TableRow
                      key={i}
                      hover
                      sx={{
                        "&:hover": { bgcolor: "#FAFBFC" },
                        cursor: "pointer",
                        transition: "background 0.15s",
                      }}
                      onClick={() => navigate(`/vendor/vendor-profile/${row?.id}`)}
                    >
                      {/* Vendor */}
                      <TableCell sx={{ borderBottom: "1px solid #f5f5f5", py: 1.8 }}>
                        <Stack direction="row" alignItems="center" gap={1.5}>
                          <Avatar
                            src={row?.avatar || ""}
                            sx={{
                              width: 38,
                              height: 38,
                              bgcolor: themeOrange,
                              fontSize: "14px",
                              fontWeight: 600,
                            }}
                          >
                            {row?.first_name?.[0]}{row?.last_name?.[0]}
                          </Avatar>
                          <Stack>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: themeBlue,
                                textTransform: "capitalize",
                                lineHeight: 1.3,
                              }}
                            >
                              {`${row?.first_name || ""} ${row?.last_name || ""}`}
                            </Typography>
                            <Typography sx={{ fontSize: "12px", color: "#999" }}>
                              ID: {row?.id}
                            </Typography>
                          </Stack>
                        </Stack>
                      </TableCell>

                      {/* Service */}
                      <TableCell sx={{ borderBottom: "1px solid #f5f5f5" }}>
                        <Typography sx={{ fontSize: "13px", color: "#555" }}>
                          {row?.services?.[0]?.service?.name || "-"}
                        </Typography>
                        {row?.services?.length > 1 && (
                          <Typography sx={{ fontSize: "11px", color: "#aaa" }}>
                            +{row.services.length - 1} more
                          </Typography>
                        )}
                      </TableCell>

                      {/* Contact */}
                      <TableCell sx={{ borderBottom: "1px solid #f5f5f5" }}>
                        <Typography sx={{ fontSize: "13px", color: "#555" }}>
                          {row?.email}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "#999" }}>
                          {row?.phone}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ borderBottom: "1px solid #f5f5f5" }}>
                        <Chip
                          label={status.label}
                          size="small"
                          sx={{
                            bgcolor: status.bg,
                            color: status.color,
                            fontWeight: 600,
                            fontSize: "11px",
                            height: "24px",
                          }}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell sx={{ borderBottom: "1px solid #f5f5f5" }}>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/vendor/vendor-profile/${row?.id}`)}
                            sx={{
                              color: themeOrange,
                              bgcolor: "#FFF0EB",
                              borderRadius: "8px",
                              "&:hover": { bgcolor: "#FFE0D5" },
                            }}
                          >
                            <IoEyeOutline size={16} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/vendor/edit-vendor/${row?.id}`)}
                            sx={{
                              color: themeBlue,
                              bgcolor: "#E8EAF6",
                              borderRadius: "8px",
                              "&:hover": { bgcolor: "#D1D5EE" },
                            }}
                          >
                            <MdEdit size={16} />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{
          borderTop: "1px solid #f0f0f0",
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
            fontSize: "12px",
            color: "#999",
          },
        }}
      />
    </Box>
  );
};
